import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Search, ChevronRight, Globe, Loader2 } from 'lucide-react';

const EMPTY_FORM = {
  name: '', organization_type: '', industry: '', website: '', phone: '',
  address: '', status: '', notes: '',
};

export default function Organizations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post } = useApi();

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const data = await get('/organizations', params);
      setOrganizations(data || []);
    } catch (e) {
      console.error('Failed to fetch organizations:', e);
    } finally {
      setLoading(false);
    }
  }, [get, search]);

  useEffect(() => {
    const t = setTimeout(fetchOrganizations, 250);
    return () => clearTimeout(t);
  }, [fetchOrganizations]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '')
      );
      const created = await post('/organizations', payload);
      toast.success('Organization created');
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      navigate(`/organizations/${created.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const canCreate = user?.role !== 'executive_viewer';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="organizations-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              Organizations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Companies and organizations across your pipeline
            </p>
          </div>
          {canCreate && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="create-organization-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Organization
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                  <DialogTitle>New Organization</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="rounded-sm"
                      data-testid="organization-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Type</Label>
                      <Input
                        value={form.organization_type}
                        onChange={(e) => setForm({ ...form, organization_type: e.target.value })}
                        placeholder="e.g. Hotel, Agency"
                        className="rounded-sm"
                        data-testid="organization-type-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Industry</Label>
                      <Input
                        value={form.industry}
                        onChange={(e) => setForm({ ...form, industry: e.target.value })}
                        className="rounded-sm"
                        data-testid="organization-industry-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Website</Label>
                      <Input
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="https://"
                        className="rounded-sm"
                        data-testid="organization-website-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Phone</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="rounded-sm"
                        data-testid="organization-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Address</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="rounded-sm"
                      data-testid="organization-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Notes</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="rounded-sm"
                      data-testid="organization-notes-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="rounded-sm"
                      data-testid="submit-organization-btn"
                    >
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Create
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm"
            data-testid="organizations-search"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : organizations.length === 0 ? (
          <Card className="rounded-sm border">
            <CardContent className="text-center py-12">
              <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No organizations found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="organizations-list">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => navigate(`/organizations/${org.id}`)}
                className="text-left"
                data-testid={`organization-card-${org.id}`}
              >
                <Card className="rounded-sm border hover:border-primary/50 hover:shadow-sm transition-all h-full">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{org.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {org.industry && (
                              <span className="text-xs text-muted-foreground truncate">{org.industry}</span>
                            )}
                            {org.is_legacy && (
                              <Badge variant="secondary" className="rounded-sm text-[10px] px-1.5 py-0">
                                From Opportunity
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                    {org.website && (
                      <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground truncate">
                        <Globe className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{org.website}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
